import { useMemo } from 'react';
import { Button, Flex, Tooltip, Typography } from 'antd';
import { useParams } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import AccountDescriptions from './AccountDescriptions';
import ClipEligibilityModal from './ClipEligibilityModal';
import CreditProtectionDetails from './CreditProtectionDetails';
import OfferEligibilityModal from '../OfferEligibilityModal';
import Referrals from './Referrals';
import BankruptcyBoundaryModal, {
  useBankruptcyBoundaryModal,
} from '../BankruptcyBoundaryModal';

import { useAccountLevelActions } from '../CreditAccountPage';
import useAccountsInfo from 'hooks/useAccountsInfo';
import useModalControls from 'hooks/useModalControls';
import { ACCOUNT_REFERRALS, GET_AUTOPAY_STATUS } from 'queries/account';
import { RE_AGE_ELIGIBILITY } from 'queries/reage';
import { formatDollarsFromCents } from 'utilities/helpers';
import { ACCOUNT_STATUS_CODE } from 'utilities/constants';

import { FIXED_PAY_REASON } from '../ApplyStatusModal/utilities/applyStatusConstants';
import { STARR_ELIGIBILITY } from 'queries/hardshipEligibility';
import HardshipEligibilityModal from './HardshipEligibilityModal';

const { Text } = Typography;

const LTPP_STATUS = {
  [FIXED_PAY_REASON.CREDIT_COUNSELING]: 'Enrolled',
  [FIXED_PAY_REASON.PAY_OFF]: 'Completed',
  [FIXED_PAY_REASON.SYSTEM_SET]: 'Failed',
};

const AssistanceDescriptions = ({ account, accountInDebtSale }) => {
  const { enableStarrEnrollment } = useFlags();
  const { autopayModal } = useAccountLevelActions();
  const { accountIsPastDue } = useAccountsInfo();
  const clipEligibilityModal = useModalControls();
  const reAgeEligibilityModal = useModalControls();
  const hardshipEligibilityModal = useModalControls();

  const {
    bankruptcyBoundaryModal,
    checkBankruptcyBoundary,
    onBankruptcyAcknowledged,
  } = useBankruptcyBoundaryModal(autopayModal.show);

  const { accountId, customerId } = useParams();

  const reAgeQuery = useQuery(RE_AGE_ELIGIBILITY, {
    variables: { customerId, accountId },
  });

  const [reAge, reAgeFailure] = useMemo(() => {
    const reAgeData = reAgeQuery.data?.account?.offers?.eligibility?.find(
      (offer) => offer.offerType === 'REAGE',
    );
    return [reAgeData, !!reAgeQuery.error];
  }, [reAgeQuery.data, reAgeQuery.error]);

  // should we be showing errors for referrals?
  const referralsQuery = useQuery(ACCOUNT_REFERRALS, {
    variables: { customerId, accountId },
  });

  const referrals = useMemo(() => {
    if (referralsQuery.error) {
      return null;
    }
    return referralsQuery.data?.account?.offers?.referral?.data || null;
  }, [referralsQuery.data, referralsQuery.error]);

  const {
    loading: starrLoading,
    error: starrError,
    data: starrData,
  } = useQuery(STARR_ELIGIBILITY, { variables: { accountId } });

  const starrEligibility = starrData?.starrEligibility;

  const { data: autopayData } = useQuery(GET_AUTOPAY_STATUS, {
    variables: { customerId, accountId },
    skip: !customerId || !accountId,
  });

  const enrolledInAutopay = autopayData?.account?.autopay?.length > 0;
  const autopayEnrollDisabled = !enrolledInAutopay && accountIsPastDue;

  const fixedPayStatus = account?.statuses?.raw?.find((status) => {
    return status.statusCode === ACCOUNT_STATUS_CODE.FIXED_PAY;
  });

  const getReAgeEligibilityText = () => {
    const isEligible = reAge?.eligible;
    const amount = reAge?.data?.amountToPay;

    if (isEligible === true) {
      return amount
        ? `Eligible (${formatDollarsFromCents(amount)})`
        : 'Eligible';
    } else {
      return 'Ineligible';
    }
  };

  const getHardshipEligibilityText = () => {
    if (starrError) {
      return <Text type="danger">Failed to retrieve</Text>;
    }

    if (!starrEligibility) {
      return null;
    }

    if (starrEligibility?.eligible) {
      return 'Eligible';
    } else {
      return 'Ineligible';
    }
  };

  let autopayDisabledReason = '';
  if (accountInDebtSale) {
    autopayDisabledReason =
      'Unable to setup autopay on this account because it’s part of a debt sale';
  } else if (autopayEnrollDisabled) {
    autopayDisabledReason =
      'Account is past due and not eligible to enroll in Autopay';
  }

  const items = [];

  items.push({
    key: 1,
    span: 2,
    label: 'Internal Autopay',
    children: (
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap="var(--spacing-xxs)"
      >
        <span data-testid="autopay-status">
          {enrolledInAutopay ? 'Enrolled' : 'Not Enrolled'}
        </span>
        <Tooltip title={autopayDisabledReason}>
          <Button
            data-testid="autopay-modal-button"
            type="link"
            size="small"
            onClick={checkBankruptcyBoundary}
            style={{ padding: 0, height: 'var(--line-height)' }}
            disabled={accountInDebtSale || autopayEnrollDisabled}
          >
            {enrolledInAutopay ? 'Manage' : 'Enroll'}
          </Button>
        </Tooltip>
      </Flex>
    ),
  });

  items.push({
    key: 2,
    span: 2,
    label: 'Re-Age Eligibility',
    children: reAgeFailure ? (
      <Text type="danger">Failed to retrieve</Text>
    ) : (
      <Flex
        align="center"
        justify="space-between"
        wrap="wrap"
        gap="var(--spacing-xxs)"
      >
        <div data-testid="reage-eligibility">{getReAgeEligibilityText()}</div>
        <Button
          data-testid="reage-modal-button"
          type="link"
          size="small"
          onClick={reAgeEligibilityModal.show}
          style={{ padding: 0 }}
        >
          View Details
        </Button>
      </Flex>
    ),
  });

  if (referrals) {
    items.push({
      key: 3,
      span: 2,
      label: 'Referral Program',
      children: <Referrals referrals={referrals} />,
    });
  }

  items.push({
    key: 4,
    span: 2,
    label: 'CLIP Eligibility',
    children: (
      <Button
        data-testid="clip-eligibility-button"
        type="link"
        size="small"
        onClick={clipEligibilityModal.show}
        style={{
          padding: 0,
        }}
      >
        View Criteria
      </Button>
    ),
  });

  items.push({
    key: 5,
    span: 2,
    label: 'Hardship Eligibility',
    children: (
      <Button
        data-testid="hardship-eligibility-button"
        onClick={() => {
          hardshipEligibilityModal.show();
        }}
        loading={starrLoading}
        disabled={starrError}
        type="link"
        style={{ padding: 0 }}
      >
        {getHardshipEligibilityText()}
      </Button>
    ),
  });

  items.push({
    key: 7,
    span: 2,
    label: (
      <>
        Credit Protection™
        <br />
        1-888-276-6443
      </>
    ),
    children: (
      <div data-testid="credit-protection">
        <CreditProtectionDetails account={account} />
      </div>
    ),
  });

  items.push({
    key: 8,
    span: 2,
    label: 'Long Term Payment Plan',
    children: (
      <div data-testid="ltpp-status">
        {fixedPayStatus
          ? LTPP_STATUS[fixedPayStatus.reasonCode]
          : 'Not Enrolled'}
      </div>
    ),
  });

  return (
    <>
      <AccountDescriptions title="Assistance" items={items} />
      <BankruptcyBoundaryModal
        onBankruptcyAcknowledged={onBankruptcyAcknowledged}
        {...bankruptcyBoundaryModal}
      />
      <ClipEligibilityModal {...clipEligibilityModal} />
      <OfferEligibilityModal
        {...reAgeEligibilityModal}
        offer="Re-Age"
        reAgeAmount={reAge?.data?.amountToPay}
        eligible={reAge?.eligible}
        eligibilityChecks={reAge?.checks}
      />
      <HardshipEligibilityModal
        {...hardshipEligibilityModal}
        eligible={starrEligibility?.eligible}
        eligibilityChecks={starrEligibility?.checks}
        hasAutopay={enrolledInAutopay}
        customerId={customerId}
        accountId={accountId}
        enableStarrEnrollment={enableStarrEnrollment}
        nextStatementCloseDate={account?.paymentInfo?.nextStatementCloseDate}
      />
    </>
  );
};

export default AssistanceDescriptions;
